import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Table, Tag, Button, Modal, Form, Select, Input, InputNumber, Switch, Upload, App, Timeline, Space, Spin, Tooltip, Tabs, Alert, Divider, Segmented } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, UploadOutlined, EditOutlined, DeleteOutlined, SaveOutlined, CloseOutlined, FileTextOutlined, TruckOutlined, DollarOutlined, FilePdfOutlined, CheckSquareOutlined, UserOutlined, CalendarOutlined, MailOutlined, LinkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import dayjs from 'dayjs';
import { AUTH_USER_CHANGED_EVENT, authService } from '../services/auth';
import api from '../services/api';
import { formatCurrency } from '../utils/currency';
import { formatLocalizedNumber, formatLocalizedPercent } from '../utils/numberFormatting';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import { formatUnit } from '../utils/units';
import { buildSafePdfFileName } from '../utils/pdfFileName';
import { Cost, OrderMarginHistoryEntry, getDisplayOrderNumber } from '../modules/orders/types';
import OrderNotificationAssignments from '../components/OrderNotificationAssignments';
import OrderCommunications from '../components/OrderCommunications';
import PresenceBanner from '../components/presence/PresenceBanner';
import { usePresence } from '../hooks/usePresence';
import { internalTaskApi, InternalTask } from '../services/internalTaskApi';
import { getAuthFileUrl } from '../utils/fileUrl';
import {
  convertQuoteToOrder,
  downloadOrderPdf,
  fetchOrderMarginExplanation,
  fetchOrderMarginHistory,
  generateWzFromOrder,
  OrderMarginExplanationDto,
  OrderDocumentLanguage,
  settleOrder,
  updateOrder,
} from '../modules/orders/api/ordersApi';
import {
  getCustomerOrderProgressStatuses,
} from '../modules/orders/customerOrderStatuses';
import {
  fetchSupplierOrders,
  fetchSupplierOrdersForCustomerOrder,
  getSupplierOrderDisplayNumber,
  linkCustomerOrder,
  SupplierOrderDto
} from '../modules/orders/api/supplierOrdersApi';
import { formatSupplierOrderQuantity } from '../modules/orders/supplierOrderUnits';
import { buildSupplierOrderLinkPayload } from '../modules/orders/supplierOrderLinkPayload';
import { MAX_COST_AMOUNT, deleteOrderCost, isManagedTransportCost, isManualCustomerOrderCost, saveOrderCost } from '../modules/orders/api/orderCostsApi';
import { downloadOrderInvoicePdf, fetchOrderInvoices, generateOrderInvoice } from '../modules/orders/api/orderInvoicesApi';
import { fetchOrderTransports, unlinkOrderFromTransport } from '../modules/orders/api/orderTransportApi';
import type { OrderTransportsDto } from '../dto/transport';
import {
  canChangeCustomerOrderStatus,
  canDeleteCustomerOrderTransportLink,
  canEditCustomerOrderDetails,
  canEditCustomerOrderDocumentFields,
  canEditCustomerOrderProducts,
  canManageCustomerOrderOperations,
  canManageCustomerOrderSupplierLinks,
  canManageOrderNotificationAssignments,
  canSettleCustomerOrder,
} from '../modules/orders/customerOrderAccessPolicy';
import {
  canShowOrderFinancialCompletenessStatus,
  canShowOrderSalePricesIncompleteHint,
} from '../modules/orders/orderFinancialCompletenessVisibility';
import {
  sendCustomerConfirmationEmail,
  previewCustomerConfirmationEmail,
  previewCustomerConfirmationEmailDraft,
  CustomerConfirmationPreview,
  sendCustomerStatusEmail,
  previewCustomerStatusEmail,
  previewCustomerStatusEmailDraft,
  CustomerStatusEmailPreview
} from '../modules/orders/api/orderStatusApi';
import {
  buildOrderDetailUpdateData,
  getOrderDetailFieldNames,
  getOrderDetailInitialValues,
  OrderDetailEditableField,
} from '../modules/orders/orderDetailEditUtils';
import { customerOrderNumberRule } from '../dto/order';
import {
  OrderStatusFormValues,
  useOrderStatusChangeWithConfirmation,
} from '../modules/orders/useOrderStatusChangeWithConfirmation';
import { useOrderDetail } from '../modules/orders/queries/useOrderDetail';
import { OrderProductsSection } from '../modules/orders/sections/OrderProductsSection';
import { promptOrderPdfConversionUnit } from '../modules/orders/orderPdfConversionPrompt';
import { OrderInfoAccordion } from '../modules/orders/sections/OrderInfoAccordion';
import { OrderMarginExplanationSection } from '../modules/orders/sections/OrderMarginExplanationSection';
import SupplierOrderLinkAllocationFields from '../modules/orders/sections/SupplierOrderLinkAllocationFields';
import { buildSupplierOrderAllocationItemOption } from '../modules/orders/supplierOrderAllocationOptions';
import CurrencyAmount from '../components/CurrencyAmount';
import OrderTransportsSection from '../modules/orders/sections/OrderTransportsSection';
import OrderEarmarksSection from '../modules/orders/sections/OrderEarmarksSection';
import CustomerEmailEditorModal from '../modules/orders/sections/CustomerEmailEditorModal';
import {
  fetchOrderFulfillmentSource,
  type OrderFulfillmentSource,
} from '../modules/orders/api/orderFulfillmentApi';
import { useSupplierOptions } from '../modules/reference-data/queries/useSupplierOptions';
import {
  DETAIL_META_GRID_STYLE,
  getOrderTypeBadgeStyle,
  getOrderTypeHeaderStyle,
  getStatusBadgeStyle,
  OrderWorkflowRail,
  PAGE_SHELL_STYLE,
  renderSectionTitle,
  SECTION_CARD_BODY_STYLE,
  SECTION_CARD_HEAD_STYLE,
  SECTION_CARD_STYLE,
} from '../modules/orders/orderDetailLayout';
import {
  CUSTOMER_ORDER_ACTIVE_STATUS_VALUES,
  getOrderAllowedTransitions,
} from '../modules/orders/orderStatusTransitions';
import OrderStatusTransitionSelect from '../modules/orders/components/OrderStatusTransitionSelect';
import { getOrderMarginTone } from '../modules/orders/orderMarginPresentation';
import './OrderDetail.css';

const isPurchaseInvoiceCost = (cost: Pick<Cost, 'category'>) => cost.category === 'PRODUCT_COST';

const buildSupplierSearchPath = (supplierName?: string) => (
  supplierName ? `/app/suppliers?search=${encodeURIComponent(supplierName)}` : '/app/suppliers'
);

const isManualSupplierAllocation = (record: SupplierOrderDto) =>
  Number(record.allocatedCostPLN || 0) > 0
  && record.effectiveAllocatedPurchaseCostPLN === undefined
  && record.effectiveAllocatedLandedCostPLN === undefined;

const getSupplierAllocationTotalCost = (record: SupplierOrderDto) => (
  Number(record.effectiveAllocatedCostPLN || record.allocatedCostPLN || 0)
);

const formatSupplierAllocationQuantity = (record: SupplierOrderDto) => {
  if (!record.allocatedQuantity) return '-';

  const unit = record.allocatedUnit || record.supplierOrderItemUnit;
  return `${Number(record.allocatedQuantity).toLocaleString('pl-PL')} ${formatUnit(unit)}`.trim();
};

const getCustomerConfirmationIssueMessage = (
  reason: string | null | undefined,
  t: (key: string) => string,
  useFallback = true,
) => {
  switch (reason) {
    case 'NO_CUSTOMER_EMAIL':
      return t('orders:orderDetail.customerHasNoEmail');
    case 'ORDER_NOT_CONFIRMED':
      return t('orders:orderDetail.customerConfirmationRequiresConfirmed');
    case 'EMAIL_NOT_CONFIGURED':
      return t('orders:orderDetail.customerConfirmationEmailNotConfigured');
    case 'EMAIL_SEND_FAILED':
      return t('orders:orderDetail.customerConfirmationError');
    default:
      return reason && useFallback ? t('orders:orderDetail.customerConfirmationBlockedFallback') : '';
  }
};

const getCustomerStatusEmailIssueMessage = (
  reason: string | null | undefined,
  t: (key: string) => string,
  useFallback = true,
) => {
  switch (reason) {
    case 'NO_CUSTOMER_EMAIL':
      return t('orders:orderDetail.customerHasNoEmail');
    case 'STATUS_EMAILS_DISABLED':
      return t('orders:orderDetail.customerStatusEmailDisabled');
    case 'EMAIL_NOT_CONFIGURED':
      return t('orders:orderDetail.customerConfirmationEmailNotConfigured');
    case 'EMAIL_SEND_FAILED':
      return t('orders:orderDetail.customerStatusEmailError');
    default:
      return reason && useFallback ? t('orders:orderDetail.customerConfirmationBlockedFallback') : '';
  }
};



const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['orders', 'common', 'tasks']);
  const presenceOthers = usePresence('ORDER', id || null);
  const { message, modal } = App.useApp();
  const { order, loading, error: orderError, refresh: refreshOrder } = useOrderDetail(id);
  const [orderTransports, setOrderTransports] = useState<OrderTransportsDto>({
    transports: [],
    productRealization: [],
  });
  const [transportLoading, setTransportLoading] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState('1');
  const [costModalVisible, setCostModalVisible] = useState(false);
  const [editingCost, setEditingCost] = useState<Cost | null>(null);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [editingField, setEditingField] = useState<OrderDetailEditableField | null>(null);
  const [editForm] = Form.useForm();
  const [savingField, setSavingField] = useState<OrderDetailEditableField | null>(null);
  const [numberForm] = Form.useForm();
  const [numberModalOpen, setNumberModalOpen] = useState(false);
  const numberFormUseManual = Form.useWatch('useManualNumbering', numberForm);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
  const [invoiceForm] = Form.useForm();
  const [supplierOrderLinkForm] = Form.useForm();
  const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
  const [orderDocumentLanguage, setOrderDocumentLanguage] = useState<OrderDocumentLanguage>(
    i18n.language.startsWith('pl') ? 'pl' : 'en'
  );
  const [orderDocumentDownloading, setOrderDocumentDownloading] = useState(false);
  const [customerEmailModalVisible, setCustomerEmailModalVisible] = useState(false);
  const [customerEmailSending, setCustomerEmailSending] = useState(false);
  const [customerEmailPreview, setCustomerEmailPreview] = useState<CustomerConfirmationPreview | null>(null);
  const [customerEmailDefaultPreview, setCustomerEmailDefaultPreview] = useState<CustomerConfirmationPreview | null>(null);
  const [customerEmailPreviewLoading, setCustomerEmailPreviewLoading] = useState(false);
  const [customerEmailSubject, setCustomerEmailSubject] = useState('');
  const [customerEmailMessage, setCustomerEmailMessage] = useState('');
  const [customerEmailEditing, setCustomerEmailEditing] = useState(false);
  const [statusEmailModalVisible, setStatusEmailModalVisible] = useState(false);
  const [statusEmailSending, setStatusEmailSending] = useState(false);
  const [statusEmailPreview, setStatusEmailPreview] = useState<CustomerStatusEmailPreview | null>(null);
  const [statusEmailDefaultPreview, setStatusEmailDefaultPreview] = useState<CustomerStatusEmailPreview | null>(null);
  const [statusEmailPreviewLoading, setStatusEmailPreviewLoading] = useState(false);
  const [statusEmailSubject, setStatusEmailSubject] = useState('');
  const [statusEmailMessage, setStatusEmailMessage] = useState('');
  const [statusEmailEditing, setStatusEmailEditing] = useState(false);
  const [marginHistory, setMarginHistory] = useState<OrderMarginHistoryEntry[]>([]);
  const [marginExplanation, setMarginExplanation] = useState<OrderMarginExplanationDto | null>(null);
  const [marginExplanationLoading, setMarginExplanationLoading] = useState(false);
  const [linkedSupplierOrders, setLinkedSupplierOrders] = useState<SupplierOrderDto[]>([]);
  const [fulfillmentSource, setFulfillmentSource] = useState<OrderFulfillmentSource | null>(null);
  const [supplierOrderLinkModalVisible, setSupplierOrderLinkModalVisible] = useState(false);
  const [supplierOrderOptions, setSupplierOrderOptions] = useState<SupplierOrderDto[]>([]);
  const [supplierOrderOptionsLoading, setSupplierOrderOptionsLoading] = useState(false);
  const [supplierOrderLinkSubmitting, setSupplierOrderLinkSubmitting] = useState(false);
  const [orderTasks, setOrderTasks] = useState<InternalTask[]>([]);
  const [orderTasksLoading, setOrderTasksLoading] = useState(false);
  const [submittingCost, setSubmittingCost] = useState(false);
  const [, setAuthRevision] = useState(0);

  const currentUser = authService.getCurrentUser();
  const canEditOrderDetails = canEditCustomerOrderDetails(currentUser);
  const canManageOrderOperations = canManageCustomerOrderOperations(currentUser);
  const canEditCustomerOrderDocuments = canEditCustomerOrderDocumentFields(currentUser);
  const canViewCustomerPayments = authService.canViewCustomerPayments();
  const canChangeOrderStatus = canChangeCustomerOrderStatus(currentUser);
  const canManageOrderCosts = authService.canManageOrderCosts();
  const canViewPurchaseDocuments = authService.canViewPurchaseDocuments();
  const canViewSalesDocuments = authService.canViewSalesDocuments();
  const canViewCustomerSalePrices = authService.canViewCustomerSalePrices();
  const canEditSalesDocuments = authService.canEditCustomerSalePrices();
  const canViewMargin = authService.canViewMargin();
  const canViewCustomerOrderFallbackCosts = authService.canViewCustomerOrderFallbackCosts();
  const canEditCustomerOrderFallbackCosts = authService.canEditCustomerOrderFallbackCosts();
  const canManageSupplierOrders = canManageCustomerOrderSupplierLinks(currentUser);
  const canViewSupplierOrderFinance = authService.canViewSupplierOrderFinance();
  const canEditSupplierOrderFinance = authService.canEditSupplierOrderFinance();
  const canEditCosts = canEditCustomerOrderFallbackCosts;
  const canEditProducts = canEditCustomerOrderProducts(currentUser);
  const canViewCustomerOrderFinancialCosts = canManageOrderCosts || canViewMargin;
  const canViewCustomerOrderCostSection = canViewCustomerOrderFinancialCosts || canViewCustomerOrderFallbackCosts;
  const canViewCustomerOrderSupplierLinks = canManageSupplierOrders;
  const canManageNotificationAssignments = canManageOrderNotificationAssignments(currentUser);
  const canDeleteTransportLink = canDeleteCustomerOrderTransportLink(currentUser);
  const canSettleDeliveredOrder = canSettleCustomerOrder(currentUser);
  const selectedSupplierOrderId = Form.useWatch('supplierOrderId', supplierOrderLinkForm);
  const {
    confirmation: statusConfirmation,
    submitting: statusSubmitting,
    submitStatusChange,
    confirmStatusChange,
    cancelConfirmation,
  } = useOrderStatusChangeWithConfirmation({
    orderId: id,
    onSuccess: async () => {
      message.success(t('orders:orderDetail.messages.statusUpdateSuccess'));
      setStatusModalVisible(false);
      statusForm.resetFields();
      await refreshOrder();
    },
    onError: () => {
      message.error(t('orders:orderDetail.messages.statusUpdateError'));
    },
  });
  const [accountManagers, setAccountManagers] = useState<Array<{ id: string; name: string; email?: string }>>([]);
  const { options: suppliers } = useSupplierOptions();
  const customerConfirmation = order?.customerConfirmation;
  const canSendCustomerConfirmation = Boolean(canManageOrderOperations && canViewSalesDocuments && customerConfirmation?.canSend);
  const customerConfirmationDisabledReason = getCustomerConfirmationIssueMessage(
    customerConfirmation?.disabledReason,
    t,
  );
  const showCustomerConfirmationBlockedReason = Boolean(
    canManageOrderOperations && canViewSalesDocuments && !canSendCustomerConfirmation && customerConfirmationDisabledReason,
  );
  const customerStatusEmail = order?.customerStatusEmail;
  const canSendCustomerStatusEmail = Boolean(canManageOrderOperations && customerStatusEmail?.canSend);
  const customerStatusEmailDisabledReason = getCustomerStatusEmailIssueMessage(
    customerStatusEmail?.disabledReason,
    t,
  );
  const useManualNumberingValue = Form.useWatch('useManualNumbering', editForm);

  useEffect(() => {
    const handleAuthUserChanged = () => setAuthRevision((revision) => revision + 1);
    window.addEventListener(AUTH_USER_CHANGED_EVENT, handleAuthUserChanged);
    return () => window.removeEventListener(AUTH_USER_CHANGED_EVENT, handleAuthUserChanged);
  }, []);

  useEffect(() => {
    refreshOrderTransports();
    fetchInvoices();
    fetchMarginHistory();
    fetchMarginExplanationData();
    fetchLinkedSupplierOrders();
    fetchFulfillmentSourceData();
    fetchOrderTasks();
    if (canEditOrderDetails) {
      fetchAccountManagers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canEditOrderDetails, canManageOrderOperations, canManageSupplierOrders, canViewMargin, canViewSalesDocuments]);

  useEffect(() => {
    if (!orderError) {
      return;
    }

    message.error(t('orders:orderDetail.messages.fetchError'));
  }, [message, orderError, t]);


  const fetchInvoices = async () => {
    if (!id || !canViewSalesDocuments) return;
    try {
      setInvoices(await fetchOrderInvoices(id));
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const fetchMarginHistory = async () => {
    if (!id || !canViewMargin) return;

    try {
      setMarginHistory(await fetchOrderMarginHistory(id));
    } catch (error) {
      console.error('Error fetching margin history:', error);
    }
  };

  const fetchMarginExplanationData = async () => {
    if (!id || !canViewMargin) return;

    setMarginExplanationLoading(true);
    try {
      setMarginExplanation(await fetchOrderMarginExplanation(id));
    } catch (error) {
      console.error('Error fetching margin explanation:', error);
      setMarginExplanation(null);
    } finally {
      setMarginExplanationLoading(false);
    }
  };

  const refreshOrderFinancials = async () => {
    await Promise.all([
      refreshOrder(),
      fetchMarginHistory(),
      fetchMarginExplanationData(),
    ]);
  };

  const fetchLinkedSupplierOrders = async () => {
    if (!id || !canManageSupplierOrders) return;

    try {
      setLinkedSupplierOrders(await fetchSupplierOrdersForCustomerOrder(id));
    } catch (error) {
      console.error('Error fetching linked supplier orders:', error);
    }
  };

  const fetchFulfillmentSourceData = async () => {
    if (!id || !canManageOrderOperations) {
      setFulfillmentSource(null);
      return;
    }

    try {
      const result = await fetchOrderFulfillmentSource(id);
      setFulfillmentSource(result.source);
    } catch (error) {
      console.error('Error fetching order fulfillment source:', error);
      setFulfillmentSource(null);
    }
  };

  const openSupplierOrderLinkModal = async () => {
    if (!canManageSupplierOrders) return;

    setSupplierOrderLinkModalVisible(true);
    supplierOrderLinkForm.resetFields();
    supplierOrderLinkForm.setFieldValue('allocateByItem', false);
    setSupplierOrderOptionsLoading(true);

    try {
      setSupplierOrderOptions(await fetchSupplierOrders());
    } catch (error) {
      message.error(t('orders:supplierOrders.messages.fetchError'));
    } finally {
      setSupplierOrderOptionsLoading(false);
    }
  };

  const closeSupplierOrderLinkModal = () => {
    setSupplierOrderLinkModalVisible(false);
    supplierOrderLinkForm.resetFields();
  };

  const handleLinkSupplierOrder = async (values: any) => {
    if (!id || !canManageSupplierOrders) return;

    setSupplierOrderLinkSubmitting(true);

    try {
      await linkCustomerOrder(values.supplierOrderId, buildSupplierOrderLinkPayload(values, {
        orderId: id,
        canEditSupplierOrderFinance,
      }));
      message.success(t('orders:supplierOrders.messages.linkSuccess'));
      closeSupplierOrderLinkModal();
      await Promise.all([
        fetchLinkedSupplierOrders(),
        fetchFulfillmentSourceData(),
        refreshOrderFinancials(),
      ]);
    } catch (error: any) {
      message.error(error?.response?.data?.message || t('orders:supplierOrders.messages.linkError'));
    } finally {
      setSupplierOrderLinkSubmitting(false);
    }
  };

  const fetchOrderTasks = async () => {
    if (!id) return;
    setOrderTasksLoading(true);
    try {
      const tasks = await internalTaskApi.getTasksByOrder(id);
      setOrderTasks(tasks);
    } catch (error) {
      console.error('Error fetching order tasks:', error);
    } finally {
      setOrderTasksLoading(false);
    }
  };

  const refreshOrderTransports = async () => {
    if (!id) return;
    setTransportLoading(true);
    try {
      setOrderTransports(await fetchOrderTransports(id));
    } catch (error: any) {
      console.error('Error fetching order transports:', error);
    } finally {
      setTransportLoading(false);
    }
  };

  const fetchAccountManagers = async () => {
    try {
      const response = await api.get('/users/options');
      setAccountManagers(response.data || []);
    } catch (error) {
      console.error('Error fetching account managers:', error);
    }
  };

  const normalizeUploadFileList = (event: any) => {
    if (Array.isArray(event)) {
      return event;
    }

    return event?.fileList || [];
  };

  const openTransportManagement = (cost?: Cost | null) => {
    setActiveTabKey('2');

    if (cost) {
      message.info(t('orders:orderDetail.managedTransportCostHint'));
    }
  };

  const closeCostModal = () => {
    setCostModalVisible(false);
    setEditingCost(null);
    form.resetFields();
  };

  const openAddCostModal = () => {
    setEditingCost(null);
    form.resetFields();
    form.setFieldsValue({
      currency: order?.currency || 'PLN',
      document: [],
    });
    setCostModalVisible(true);
  };

  const openEditCostModal = (cost: Cost) => {
    if (isManagedTransportCost(cost)) {
      openTransportManagement(cost);
      return;
    }

    // If cost was entered in a foreign currency, show the original amount;
    // otherwise show the stored PLN amount directly.
    const displayAmount = cost.originalAmount != null && cost.originalCurrency && cost.originalCurrency !== 'PLN'
      ? cost.originalAmount
      : cost.amount;

    setEditingCost(cost);
    form.resetFields();
    form.setFieldsValue({
      currency: cost.originalCurrency || 'PLN',
      amount: displayAmount,
      description: cost.description,
      document: [],
    });
    setCostModalVisible(true);
  };

  const handleSubmitCost = async (values: any) => {
    if (!id) return;

    setSubmittingCost(true);

    try {
      await saveOrderCost({
        orderId: id,
        costId: editingCost?.id,
        values,
      });

      message.success(
        editingCost
          ? t('orders:orderDetail.messages.costUpdatedSuccess')
          : t('orders:orderDetail.messages.costAddedSuccess')
      );
      closeCostModal();
      await refreshOrderFinancials();
    } catch (error) {
      message.error(
        editingCost
          ? t('orders:orderDetail.messages.costUpdateError')
          : t('orders:orderDetail.messages.costAddError')
      );
    } finally {
      setSubmittingCost(false);
    }
  };

  const handleAddCost = async (values: any) => {
    await handleSubmitCost(values);
  };

  const handleDeleteCost = async (cost: Cost) => {
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('orders:orderDetail.deleteCostConfirmTitle'),
        content: t('orders:orderDetail.deleteCostConfirmDescription'),
        okText: t('orders:orderDetail.delete'),
        okType: 'danger',
        cancelText: t('common:cancel'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteOrderCost(cost.id);
      message.success(t('orders:orderDetail.messages.costDeletedSuccess'));
      await refreshOrderFinancials();
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || t('orders:orderDetail.messages.costDeleteError')
      );
    }
  };

  const handleSendCustomerConfirmation = async () => {
    if (!id) return;
    setCustomerEmailSending(true);
    try {
      const language = orderDocumentLanguage === 'en' ? 'en' : 'pl';
      const response = await sendCustomerConfirmationEmail(id, {
        language,
        ...(customerEmailEditing
          ? { subject: customerEmailSubject, message: customerEmailMessage }
          : {}),
      });
      message.success(t('orders:orderDetail.customerConfirmationSentTo', { email: response.recipient }));
      setCustomerEmailModalVisible(false);
      setCustomerEmailEditing(false);
    } catch (error: any) {
      const serverCode = error?.response?.data?.code;
      const serverMessage = error?.response?.data?.message;
      const issueMessage = getCustomerConfirmationIssueMessage(serverCode, t, false);
      message.error(issueMessage || serverMessage || t('orders:orderDetail.customerConfirmationError'));
    } finally {
      setCustomerEmailSending(false);
    }
  };

  useEffect(() => {
    if (!customerEmailModalVisible || !id) {
      return;
    }
    let cancelled = false;
    setCustomerEmailPreviewLoading(true);
    setCustomerEmailPreview(null);
    setCustomerEmailDefaultPreview(null);
    setCustomerEmailSubject('');
    setCustomerEmailMessage('');
    setCustomerEmailEditing(false);
    const language = orderDocumentLanguage === 'en' ? 'en' : 'pl';
    previewCustomerConfirmationEmail(id, language)
      .then((preview) => {
        if (!cancelled) {
          setCustomerEmailPreview(preview);
          setCustomerEmailDefaultPreview(preview);
          setCustomerEmailSubject(preview.subject || '');
          setCustomerEmailMessage(preview.message || '');
        }
      })
      .catch(() => {
        if (!cancelled) message.error(t('orders:orderDetail.customerConfirmationPreviewError'));
      })
      .finally(() => {
        if (!cancelled) setCustomerEmailPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [customerEmailModalVisible, id, message, orderDocumentLanguage, t]);

  useEffect(() => {
    if (!customerEmailModalVisible || !customerEmailEditing || !id) {
      return;
    }
    if (!customerEmailSubject.trim() || !customerEmailMessage.trim()) {
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      const language = orderDocumentLanguage === 'en' ? 'en' : 'pl';
      previewCustomerConfirmationEmailDraft(id, {
        language,
        subject: customerEmailSubject,
        message: customerEmailMessage
      })
        .then((preview) => {
          if (!cancelled) setCustomerEmailPreview(preview);
        })
        .catch(() => {
          if (!cancelled) message.error(t('orders:orderDetail.customerConfirmationPreviewError'));
        });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    customerEmailEditing,
    customerEmailMessage,
    customerEmailModalVisible,
    customerEmailSubject,
    id,
    message,
    orderDocumentLanguage,
    t
  ]);

  const handleSendCustomerStatusEmail = async () => {
    if (!id) return;
    setStatusEmailSending(true);
    try {
      const response = await sendCustomerStatusEmail(id, {
        ...(statusEmailEditing
          ? { subject: statusEmailSubject, message: statusEmailMessage }
          : {}),
      });
      message.success(t('orders:orderDetail.customerStatusEmailSentTo', { email: response.recipient }));
      setStatusEmailModalVisible(false);
      setStatusEmailEditing(false);
    } catch (error: any) {
      const serverCode = error?.response?.data?.code;
      const serverMessage = error?.response?.data?.message;
      const issueMessage = getCustomerStatusEmailIssueMessage(serverCode, t, false);
      message.error(issueMessage || serverMessage || t('orders:orderDetail.customerStatusEmailError'));
    } finally {
      setStatusEmailSending(false);
    }
  };

  useEffect(() => {
    if (!statusEmailModalVisible || !id) {
      return;
    }
    let cancelled = false;
    setStatusEmailPreviewLoading(true);
    setStatusEmailPreview(null);
    setStatusEmailDefaultPreview(null);
    setStatusEmailSubject('');
    setStatusEmailMessage('');
    setStatusEmailEditing(false);
    previewCustomerStatusEmail(id)
      .then((preview) => {
        if (!cancelled) {
          setStatusEmailPreview(preview);
          setStatusEmailDefaultPreview(preview);
          setStatusEmailSubject(preview.subject || '');
          setStatusEmailMessage(preview.message || '');
        }
      })
      .catch(() => {
        if (!cancelled) message.error(t('orders:orderDetail.customerStatusEmailPreviewError'));
      })
      .finally(() => {
        if (!cancelled) setStatusEmailPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [statusEmailModalVisible, id, message, t]);

  useEffect(() => {
    if (!statusEmailModalVisible || !statusEmailEditing || !id) {
      return;
    }
    if (!statusEmailSubject.trim() || !statusEmailMessage.trim()) {
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      previewCustomerStatusEmailDraft(id, {
        subject: statusEmailSubject,
        message: statusEmailMessage
      })
        .then((preview) => {
          if (!cancelled) setStatusEmailPreview(preview);
        })
        .catch(() => {
          if (!cancelled) message.error(t('orders:orderDetail.customerStatusEmailPreviewError'));
        });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    id,
    message,
    statusEmailEditing,
    statusEmailMessage,
    statusEmailModalVisible,
    statusEmailSubject,
    t
  ]);

  const handleUpdateStatus = async (values: OrderStatusFormValues) => {
    await submitStatusChange(values);
  };

  const handleGenerateInvoice = async (values: any) => {
    if (!id) return;

    setInvoiceSubmitting(true);
    try {
      const payload: Parameters<typeof generateOrderInvoice>[0] = {
        orderId: id,
        type: values.type,
        currency: order?.currency || 'PLN'
      };

      if (values.totalAmount !== undefined && values.totalAmount !== null) {
        payload.totalAmount = values.totalAmount;
      }

      await generateOrderInvoice(payload);
      message.success(t('orders:orderDetail.invoiceGenerated'));
      setInvoiceModalVisible(false);
      invoiceForm.resetFields();
      await fetchInvoices();
    } catch (error: any) {
      message.error(error.response?.data?.error || t('orders:orderDetail.invoiceGenerateFailed'));
    } finally {
      setInvoiceSubmitting(false);
    }
  };

  const handleDownloadInvoicePDF = async (invoiceId: string, invoiceNumber: string) => {
    try {
      const invoiceBlob = await downloadOrderInvoicePdf(invoiceId);
      const url = window.URL.createObjectURL(new Blob([invoiceBlob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceNumber.replace(/\//g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      message.error(t('orders:orderDetail.invoiceDownloadFailed'));
    }
  };

  const handleDownloadOrderDocument = async () => {
    if (!id || !order || !canViewSalesDocuments) return;

    const conversionUnit = await promptOrderPdfConversionUnit(modal, t);

    if (!conversionUnit) return;

    setOrderDocumentDownloading(true);
    try {
      const orderBlob = await downloadOrderPdf(id, orderDocumentLanguage, conversionUnit);
      const url = window.URL.createObjectURL(new Blob([orderBlob]));
      const link = document.createElement('a');
      const filePrefix = orderDocumentLanguage === 'pl' ? 'zamowienie' : 'order';
      link.href = url;
      link.setAttribute('download', `${buildSafePdfFileName(filePrefix, getDisplayOrderNumber(order))}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success(t('orders:orderDetail.orderDocumentDownloaded'));
    } catch (error) {
      message.error(t('orders:orderDetail.orderDocumentDownloadFailed'));
    } finally {
      setOrderDocumentDownloading(false);
    }
  };

  // Previously this called DELETE /transport-routes/:id which removed the
  // whole transport — including any other orders riding on it. Transports
  // are N:N; unlinking one order must not cascade. Now we call the unlink
  // endpoint, which only drops the join row for this order. Backend still
  // cleans up the transport when it becomes empty (returns { deleted: true }).
  const handleUnlinkTransport = async (transportId: string) => {
    if (!id) return;
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('transport:orderTransports.confirmUnlinkTitle'),
        content: t('transport:orderTransports.confirmUnlinkContent'),
        okText: t('transport:orderTransports.unlink'),
        okType: 'danger',
        cancelText: t('common:cancel'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) {
      return;
    }

    try {
      await unlinkOrderFromTransport(transportId, id);
      message.success(t('transport:orderTransports.unlinkSuccess'));
      void refreshOrderTransports();
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || t('transport:orderTransports.unlinkError')
      );
    }
  };

  const handleConvertQuote = async () => {
    if (!id) return;

    try {
      await convertQuoteToOrder(id);
      message.success(t('orders:orderDetail.quoteConverted'));
      await refreshOrder();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('orders:orderDetail.quoteConvertError'));
    }
  };

  const handleSettleOrder = async () => {
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('orders:orderDetail.confirmSettle'),
        content: t('orders:orderDetail.confirmSettleContent'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) {
      return;
    }

    if (!id) return;

    try {
      await settleOrder(id);
      message.success(t('orders:orderDetail.settledSuccess'));
      await refreshOrderFinancials();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('orders:orderDetail.settleError'));
    }
  };

  const startEditing = (field: OrderDetailEditableField) => {
    if (!order) return;

    editForm.resetFields();
    setEditingField(field);
    editForm.setFieldsValue(getOrderDetailInitialValues(order, field));
  };

  const cancelEditing = (field: OrderDetailEditableField) => {
    if (editingField !== field) return;

    setEditingField(null);
    editForm.resetFields();
  };

  const saveField = async (field: OrderDetailEditableField) => {
    if (!id) return;

    try {
      const values = editForm.getFieldsValue(true);
      await editForm.validateFields(getOrderDetailFieldNames(field, values));
      setSavingField(field);
      const updateData = buildOrderDetailUpdateData(field, values);

      await updateOrder(id, updateData);
      message.success(t('orders:orderDetail.messages.updateSuccess'));
      cancelEditing(field);
      await refreshOrder();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.response?.data?.message || t('orders:orderDetail.messages.updateError'));
    } finally {
      setSavingField(null);
    }
  };

  const saveOrderNumber = async () => {
    if (!id) return;
    try {
      const values = numberForm.getFieldsValue(true);
      await numberForm.validateFields(getOrderDetailFieldNames('manualNumbering', values));
      setSavingField('manualNumbering');
      await updateOrder(id, buildOrderDetailUpdateData('manualNumbering', values));
      message.success(t('orders:orderDetail.messages.updateSuccess'));
      setNumberModalOpen(false);
      await refreshOrder();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || t('orders:orderDetail.messages.updateError'));
    } finally {
      setSavingField(null);
    }
  };

  const getNumberModalInitialValues = (): Record<string, unknown> => {
    if (!order) return {};
    const init = getOrderDetailInitialValues(order, 'manualNumbering');
    if (!order.useManualNumbering) {
      init.manualOrderNumber = getDisplayOrderNumber(order);
    }
    return init;
  };

  const renderEditableField = (
    field: OrderDetailEditableField,
    value: React.ReactNode,
    renderComponent: () => React.ReactNode,
    canEditField = canEditOrderDetails
  ) => {
    if (!canEditField) return value;

    const isEditing = editingField === field;

    if (isEditing) {
      return (
        <div style={{ display: 'grid', gap: 10, width: '100%' }}>
          {renderComponent()}
          <Space wrap>
            <Button
              icon={<SaveOutlined />}
              size="small"
              type="primary"
              loading={savingField === field}
              onClick={() => saveField(field)}
            >
              {t('common:save')}
            </Button>
            <Button
              icon={<CloseOutlined />}
              size="small"
              onClick={() => cancelEditing(field)}
              disabled={savingField === field}
            >
              {t('common:cancel')}
            </Button>
          </Space>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>{value}</div>
        <Tooltip title={t('orders:orderDetail.edit')}>
          <Button
            aria-label={t('orders:orderDetail.edit')}
            icon={<EditOutlined />}
            size="small"
            type="text"
            shape="circle"
            onClick={() => startEditing(field)}
            disabled={Boolean(editingField && editingField !== field)}
            style={{ flex: '0 0 auto', marginTop: -3, color: '#475569' }}
          />
        </Tooltip>
      </div>
    );
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (!order) {
    return <div>{t('orders:orderDetail.orderNotFound')}</div>;
  }

  const linkedSupplierOrderIds = new Set(linkedSupplierOrders.map((supplierOrder) => supplierOrder.id));
  const availableSupplierOrders = supplierOrderOptions.filter(
    (supplierOrder) => !linkedSupplierOrderIds.has(supplierOrder.id)
  );
  const selectedSupplierOrderForLink = availableSupplierOrders.find(
    (supplierOrder) => supplierOrder.id === selectedSupplierOrderId
  );
  const selectedSupplierOrderItemOptions = (selectedSupplierOrderForLink?.items || []).map((item) => (
    buildSupplierOrderAllocationItemOption(
      item,
      item.id,
      `${item.name} (${formatSupplierOrderQuantity(item, t, i18n.language)})`,
    )
  ));

  const costColumns = [
    {
      title: t('orders:orderDetail.category'),
      dataIndex: 'category',
      key: 'category',
      render: (cat: string) => t(`orders:costCategories.${cat}`),
    },
    {
      title: t('orders:orderDetail.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: any) => (
        <CurrencyAmount
          amount={record.originalCurrency && record.originalCurrency !== 'PLN' && record.originalAmount != null ? record.originalAmount : amount}
          currency={record.originalCurrency || 'PLN'}
          plnAmount={amount}
          rateDate={record.incurredAt}
        />
      ),
    },
    {
      title: t('orders:orderDetail.purchaseInvoice'),
      key: 'purchaseInvoice',
      render: (_: unknown, record: Cost) => {
        if (record.category !== 'PRODUCT_COST') {
          return '-';
        }

        if (!record.purchaseInvoiceNumber && !record.purchaseInvoiceDate) {
          return '-';
        }

        return (
          <div>
            {record.purchaseInvoiceNumber && (
              <div>
                {record.purchaseInvoiceNumber}
              </div>
            )}
            {record.purchaseInvoiceDate && (
              <div style={{ fontSize: '0.9em', color: '#666' }}>
                {formatDate(record.purchaseInvoiceDate)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('orders:orderDetail.costDescription'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('orders:orderDetail.addedBy'),
      dataIndex: ['addedBy', 'name'],
      key: 'addedBy',
    },
    {
      title: t('orders:orderDetail.date'),
      dataIndex: 'incurredAt',
      key: 'incurredAt',
      render: (date: string) => formatDate(date),
    },
    ...(canEditCosts ? [{
      title: t('orders:orderDetail.actions'),
      key: 'actions',
      width: 140,
      render: (_: any, record: Cost) => (
        <Space size="small">
          <Button
            size="small"
            icon={isManagedTransportCost(record) ? <TruckOutlined /> : <EditOutlined />}
            disabled={
              (isManagedTransportCost(record) && !canManageOrderCosts)
              || (!isManagedTransportCost(record) && !canEditCustomerOrderFallbackCosts)
              || (!isManagedTransportCost(record) && !isManualCustomerOrderCost(record))
            }
            onClick={() => (
              isManagedTransportCost(record)
                ? openTransportManagement(record)
                : openEditCostModal(record)
            )}
          >
            {isManagedTransportCost(record) ? t('transport:editTransport') : t('common:edit')}
          </Button>
          {isManualCustomerOrderCost(record) && canEditCustomerOrderFallbackCosts && (
            <Tooltip title={t('orders:orderDetail.delete')}>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                aria-label={t('orders:orderDetail.delete')}
                onClick={() => handleDeleteCost(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    }] : []),
  ];
  const operationalCostColumns = costColumns.filter((column: any) => column.key !== 'purchaseInvoice');
  const purchaseInvoiceCostColumns = costColumns;

  const invoiceColumns = [
    {
      title: t('orders:orderDetail.invoiceColumns.number'),
      dataIndex: 'number',
      key: 'number',
    },
    {
      title: t('orders:orderDetail.invoiceColumns.type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={type === 'FINAL' ? 'green' : type === 'PROFORMA' ? 'blue' : 'orange'}>
          {t(`orders:orderDetail.invoiceTypes.${type}`)}
        </Tag>
      ),
    },
    {
      title: t('orders:orderDetail.invoiceColumns.amount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number, record: any) => formatCurrency(amount, record.currency),
    },
    {
      title: t('orders:orderDetail.invoiceColumns.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'PAID' ? 'green' : status === 'ISSUED' ? 'blue' : status === 'CANCELLED' ? 'red' : 'default'}>
          {t(`orders:orderDetail.invoiceStatus.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('orders:orderDetail.invoiceColumns.issueDate'),
      dataIndex: 'issueDate',
      key: 'issueDate',
      render: (date: string) => formatDate(date),
    },
    {
      title: t('orders:orderDetail.invoiceColumns.actions'),
      key: 'actions',
      render: (_: any, record: any) => (
        <Button
          icon={<FilePdfOutlined />}
          size="small"
          onClick={() => handleDownloadInvoicePDF(record.id, record.number)}
        >
          PDF
        </Button>
      ),
    },
  ];

  const customerName = order.customer?.name || t('orders:orderDetail.archivedCustomer');
  const createdByName = order.createdBy?.name || t('orders:orderDetail.deletedUser');
  const orderCosts = Array.isArray(order.costs) ? order.costs : [];
  const operationalCosts = orderCosts.filter((cost) => (
    isManualCustomerOrderCost(cost) || isManagedTransportCost(cost)
  ));
  const customerFallbackCosts = orderCosts.filter(isManualCustomerOrderCost);
  const customerOrderCostRows = canViewCustomerOrderFinancialCosts
    ? operationalCosts
    : customerFallbackCosts;
  const purchaseInvoiceCosts = orderCosts.filter(isPurchaseInvoiceCost);
  const orderStatusHistory = Array.isArray(order.statusHistory) ? order.statusHistory : [];
  // Money is stored in PLN (migration 054). Display stored values directly.
  const toDisplayAmount = (amount: number | null | undefined) => Number(amount ?? 0);
  const getCostAmountInPln = (cost: any) => toDisplayAmount(cost?.amount);
  const revenueDisplayAmount = toDisplayAmount(order.revenue);
  const totalCostDisplayAmount = toDisplayAmount(order.totalCost);
  const marginDisplayAmount = toDisplayAmount(order.margin);
  const plannedMarginDisplayAmount = toDisplayAmount(order.plannedMargin);
  const settledMarginDisplayAmount = toDisplayAmount(order.settledMargin);
  const statusBadgeStyle = getStatusBadgeStyle(order.status);
  const marginPercent = order.marginPercent || 0;
  const marginTone = getOrderMarginTone(marginDisplayAmount, marginPercent);
  const marginStatusKey = marginTone === 'loss'
    ? 'marginResultNegative'
    : marginTone === 'watch'
      ? 'marginResultWatch'
      : 'marginResultPositive';
  const marginPercentLabel = marginPercent.toLocaleString(
    i18n.language.startsWith('pl') ? 'pl-PL' : 'en-US',
    { minimumFractionDigits: 1, maximumFractionDigits: 1 },
  );
  const marginDeviation =
    order.plannedMarginPercent != null
      ? (order.marginPercent || 0) - (order.plannedMarginPercent || 0)
      : null;
  const isFinancialCompletenessPending = canShowOrderFinancialCompletenessStatus(
    canViewCustomerSalePrices,
    order.financialCompleteness,
  );
  const salePricesIncomplete = canShowOrderSalePricesIncompleteHint(
    canViewCustomerSalePrices,
    order.financialCompleteness,
  );
  const cogsDisplayAmount = toDisplayAmount(order.cogsPLN);
  const customerPaymentStatus = order.customerPaymentStatus || 'PENDING';
  const customerPaymentDueDate = order.customerPaymentDueDate ? dayjs(order.customerPaymentDueDate) : null;
  const isCustomerPaymentOverdue = Boolean(
    customerPaymentStatus !== 'PAID' &&
    customerPaymentDueDate &&
    customerPaymentDueDate.isBefore(dayjs(), 'day')
  );
  const customerAccentColor = '#0f766e';
  const customerAccentBackground = '#ecfdf5';
  const customerWorkflowStatuses = getCustomerOrderProgressStatuses(order.status, orderStatusHistory);
  const customerWorkflowCurrentKey = order.status;
  const isCustomerWorkflowCancelled = order.status === 'CANCELLED';
  const isCustomerWorkflowHeld = order.status === 'ON_HOLD';
  const customerWorkflowSteps = customerWorkflowStatuses.map((status) => ({
    key: status,
    label: t(`orders:statusOptions.${status}`),
  }));
  const customerWorkflowCurrentLabel = t(
    isCustomerWorkflowCancelled
      ? 'orders:orderDetail.cancelledWorkflow'
      : isCustomerWorkflowHeld
        ? 'orders:orderDetail.heldWorkflow'
        : 'orders:orderDetail.currentStatus'
  );
  const overviewMeta = [
    {
      icon: <UserOutlined />,
      label: t('orders:orderDetail.customer'),
      value: customerName,
    },
    {
      icon: <UserOutlined />,
      label: t('orders:orderDetail.accountManager'),
      value: order.accountManagerName || createdByName || '-',
    },
    {
      icon: <CalendarOutlined />,
      label: t('orders:orderDetail.orderDate'),
      value: formatDate(order.orderDate),
    },
    {
      icon: <TruckOutlined />,
      label: t('orders:orderDetail.expectedDelivery'),
      value: formatDate(order.expectedDelivery),
    },
    ...(canManageOrderOperations && fulfillmentSource
      ? [{
          icon: <CheckSquareOutlined />,
          label: t('orders:orderDetail.fulfillmentSource'),
          value: (
            <Tag color={fulfillmentSource === 'MIXED' ? 'purple' : fulfillmentSource === 'WAREHOUSE' ? 'blue' : fulfillmentSource === 'SUPPLIER' ? 'cyan' : 'default'}>
              {t(`orders:orderDetail.fulfillmentSources.${fulfillmentSource}`)}
            </Tag>
          ),
        }]
      : []),
  ];

  return (
    <div style={{ paddingBottom: 24 }}>
      <div style={PAGE_SHELL_STYLE}>
      <PresenceBanner scopeType="ORDER" others={presenceOthers} style={{ marginBottom: 16 }} />
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        style={getOrderTypeHeaderStyle(customerAccentColor)}
      >
        <div style={{ flex: '1 1 100%', minWidth: 0 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/app/orders')}
            style={{ marginBottom: 12, borderRadius: 999, fontWeight: 600 }}
          >
            {t('orders:orderDetail.backToOrders')}
          </Button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <span style={getOrderTypeBadgeStyle(customerAccentColor, customerAccentBackground)}>
              <UserOutlined />
              {t('orders:orderDetail.customerOrderType')}
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>
              {t('orders:orderDetail.title')}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <div style={{ fontSize: 'clamp(1.45rem, 2.8vw, 2.3rem)', lineHeight: 1.05, fontWeight: 800, letterSpacing: 0, color: '#0f172a', overflowWrap: 'anywhere' }}>
              {getDisplayOrderNumber(order)}
            </div>
            {canManageOrderOperations && (
              <Tooltip title={t('orders:orderDetail.editNumber')}>
                <Button
                  aria-label={t('orders:orderDetail.editNumber')}
                  icon={<EditOutlined />}
                  type="text"
                  size="small"
                  shape="circle"
                  onClick={() => setNumberModalOpen(true)}
                  style={{ color: '#475569' }}
                />
              </Tooltip>
            )}
            <Modal
              open={numberModalOpen}
              title={t('orders:orderDetail.editNumber')}
              onCancel={() => setNumberModalOpen(false)}
              onOk={saveOrderNumber}
              okText={t('common:save')}
              cancelText={t('common:cancel')}
              confirmLoading={savingField === 'manualNumbering'}
              destroyOnClose
            >
              <Form form={numberForm} layout="vertical" initialValues={getNumberModalInitialValues()}>
                <Form.Item
                  name="useManualNumbering"
                  valuePropName="checked"
                  label={t('orders:orderDetail.useManualNumbering')}
                >
                  <Switch />
                </Form.Item>
                {numberFormUseManual && (
                  <Form.Item
                    name="manualOrderNumber"
                    label={t('orders:orderNumber')}
                    rules={[
                      { required: true, whitespace: true, message: t('orders:modal.validation.manualNumberRequired') },
                      customerOrderNumberRule(t),
                    ]}
                  >
                    <Input maxLength={64} placeholder={t('orders:orderDetail.manualOrderNumberPlaceholder')} />
                  </Form.Item>
                )}
              </Form>
            </Modal>
            {order.useManualNumbering && (
              <Tag color="blue" style={{ borderRadius: 999, paddingInline: 10, fontWeight: 600 }}>
                {t('orders:orderDetail.manual')}
              </Tag>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <span style={statusBadgeStyle}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: 'currentColor' }} />
              {t(`orders:statusOptions.${order.status}`)}
            </span>
            {isFinancialCompletenessPending && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderRadius: 999,
                  fontWeight: 700,
                  fontSize: 13,
                  background: '#fffbeb',
                  border: '1px solid #fde68a',
                  color: '#92400e',
                }}
              >
                {t('orders:orderDetail.financialCompletenessPending')}
              </span>
            )}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 12px',
                borderRadius: 999,
                fontWeight: 700,
                fontSize: 13,
                background: '#ffffff',
                border: '1px solid #dbe7f3',
                color: '#334155',
              }}
            >
              {order.currency || 'PLN'}
            </span>
            {canChangeOrderStatus && (
              <Button type="primary" onClick={() => setStatusModalVisible(true)} style={{ borderRadius: 999, fontWeight: 600 }}>
                {t('orders:orderDetail.updateStatus')}
              </Button>
            )}
            {canManageOrderOperations && canViewSalesDocuments && (
              <Tooltip title={customerConfirmationDisabledReason}>
                <Button
                  icon={<MailOutlined />}
                  disabled={!canSendCustomerConfirmation}
                  onClick={() => setCustomerEmailModalVisible(true)}
                  style={{ borderRadius: 999, fontWeight: 600 }}
                >
                  {t('orders:orderDetail.sendCustomerConfirmation')}
                </Button>
              </Tooltip>
            )}
            {canManageOrderOperations && (
              <Tooltip title={canSendCustomerStatusEmail ? '' : customerStatusEmailDisabledReason}>
                <Button
                  icon={<MailOutlined />}
                  disabled={!canSendCustomerStatusEmail}
                  onClick={() => setStatusEmailModalVisible(true)}
                  style={{ borderRadius: 999, fontWeight: 600 }}
                >
                  {t('orders:orderDetail.sendCustomerStatusEmail')}
                </Button>
              </Tooltip>
            )}
            {canViewSalesDocuments && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 8,
                  padding: '6px 8px',
                  borderRadius: 999,
                  background: '#ffffff',
                  border: '1px solid #dbe7f3',
                  maxWidth: '100%',
                }}
              >
                <Segmented<OrderDocumentLanguage>
                  size="small"
                  value={orderDocumentLanguage}
                  onChange={(value) => setOrderDocumentLanguage(value as OrderDocumentLanguage)}
                  options={[
                    { label: 'EN', value: 'en' },
                    { label: 'PL', value: 'pl' },
                  ]}
                />
                <Button
                  icon={<FilePdfOutlined />}
                  loading={orderDocumentDownloading}
                  onClick={handleDownloadOrderDocument}
                  style={{ borderRadius: 999, fontWeight: 600, maxWidth: '100%', whiteSpace: 'normal', height: 'auto' }}
                >
                  {t('orders:orderDetail.downloadOrderDocument')}
                </Button>
              </div>
            )}
          </div>
          {showCustomerConfirmationBlockedReason && (
            <Alert
              type="warning"
              showIcon
              message={customerConfirmationDisabledReason}
              style={{ marginBottom: 12, borderRadius: 12 }}
            />
          )}

          <div style={DETAIL_META_GRID_STYLE}>
            {overviewMeta.map((item) => (
              <div
                key={item.label}
                style={{
                  display: 'flex',
                  gap: 10,
                  padding: '12px 14px',
                  borderRadius: 14,
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                }}
              >
                <div style={{ fontSize: 15, color: '#2563eb', marginTop: 1 }}>{item.icon}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>{item.label}</div>
                  <div style={{ color: '#0f172a', fontSize: 15, fontWeight: 600, lineHeight: 1.4, wordBreak: 'break-word' }}>{item.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {order.isQuote && (
        <Alert
          type="info"
          showIcon
          message={t('orders:orderDetail.quoteBanner')}
          action={
            canManageOrderOperations && (
              <Button size="small" type="primary" onClick={handleConvertQuote}>
                {t('orders:orderDetail.convertToOrder')}
              </Button>
            )
          }
          style={{ marginBottom: 16 }}
        />
      )}
      {order.quoteConvertedAt && !order.isQuote && (
        <Alert
          type="success"
          showIcon
          message={t('orders:orderDetail.convertedFromQuote', { date: formatDate(order.quoteConvertedAt) })}
          style={{ marginBottom: 16 }}
        />
      )}

      <OrderWorkflowRail
        title={t('orders:orderDetail.workflow')}
        steps={customerWorkflowSteps}
        currentKey={customerWorkflowCurrentKey}
        currentLabel={customerWorkflowCurrentLabel}
        cancelled={isCustomerWorkflowCancelled}
        cancelledText={t('orders:orderDetail.cancelledWorkflow')}
        held={isCustomerWorkflowHeld}
        heldText={t('orders:orderDetail.heldWorkflow')}
      />

      <Tabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        size="large"
        style={{ maxWidth: '100%', minWidth: 0 }}
        tabBarStyle={{ marginBottom: 24, fontWeight: 600, maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}
      >
        <Tabs.TabPane tab={t('orders:orderDetail.orderInfo')} key="1">
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.03 }}>
              <OrderProductsSection
                order={order}
                canEditProducts={canEditProducts}
                canViewSalePrices={canViewCustomerSalePrices}
                canEditSalePrices={canEditSalesDocuments}
                salePricesIncomplete={salePricesIncomplete}
                onSaved={refreshOrderFinancials}
              />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 }}>
              <OrderInfoAccordion
                order={order}
                customerName={customerName}
                createdByName={createdByName}
                accountManagers={accountManagers}
                suppliers={suppliers}
                customerPaymentStatus={customerPaymentStatus}
                canViewCustomerPayments={canViewCustomerPayments}
                isCustomerPaymentOverdue={isCustomerPaymentOverdue}
                useManualNumberingValue={Boolean(useManualNumberingValue)}
                editingField={editingField}
                editForm={editForm}
                renderEditableField={renderEditableField}
                canManageOrderOperations={canManageOrderOperations}
                canEditCustomerOrderDocuments={canEditCustomerOrderDocuments}
              />
            </motion.div>

            {/* Earmarks section — ADMIN/OPERATIONS only */}
            {canManageOrderOperations && id && (
              <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.12 }}>
                <OrderEarmarksSection orderId={id} onChanged={fetchFulfillmentSourceData} />
              </motion.div>
            )}
          </Space>
        </Tabs.TabPane>

        <Tabs.TabPane
          tab={
            <span>
              <FileTextOutlined />
              {t('orders:orderDetail.activity')}
            </span>
          }
          key="activity"
        >
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.03 }}>
              <Card
                title={renderSectionTitle(t('orders:orderDetail.statusHistory'))}
                style={SECTION_CARD_STYLE}
                headStyle={SECTION_CARD_HEAD_STYLE}
                bodyStyle={{ ...SECTION_CARD_BODY_STYLE, paddingTop: 16 }}
              >
                <Timeline style={{ marginBottom: 0 }}>
                  {orderStatusHistory.map((history: any) => (
                    <Timeline.Item key={history.id}>
                      <div>
                        <strong>{t(`orders:statusOptions.${history.status}`)}</strong>
                        <div style={{ fontSize: 12, color: '#999' }}>
                          {formatDateTime(history.changedAt)}
                        </div>
                        {history.notes && <div style={{ fontSize: 12 }}>{history.notes}</div>}
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Card>
            </motion.div>

            {/* Notification Assignments - Admin only */}
            {canManageNotificationAssignments && (
              <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.06 }}>
                <OrderNotificationAssignments orderId={id!} />
              </motion.div>
            )}

            {/* Communications sent for this order - all roles */}
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.09 }}>
              <OrderCommunications orderId={id!} />
            </motion.div>
          </Space>
        </Tabs.TabPane>

    <Tabs.TabPane
      tab={
        <span>
          <TruckOutlined />
          {t('transport:transport')}
          {orderTransports.transports.length > 0 && (
            <Tag style={{ marginLeft: 8 }}>{orderTransports.transports.length}</Tag>
          )}
        </span>
      }
      key="2"
    >
      <OrderTransportsSection
        orderId={id!}
        orderTransports={orderTransports}
        loading={transportLoading}
        canEdit={canManageOrderOperations}
        canDelete={canDeleteTransportLink}
        onDelete={handleUnlinkTransport}
      />
    </Tabs.TabPane>

    {canViewCustomerOrderSupplierLinks && (
    <Tabs.TabPane
      tab={
        <span>
          <LinkOutlined />
          {t('orders:orderDetail.relations')}
          {linkedSupplierOrders.length > 0 && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {linkedSupplierOrders.length}
            </Tag>
          )}
        </span>
      }
      key="3"
    >
      <Card
          title={renderSectionTitle(t('orders:orderDetail.linkedSupplierOrders'))}
          style={SECTION_CARD_STYLE}
          headStyle={SECTION_CARD_HEAD_STYLE}
          bodyStyle={SECTION_CARD_BODY_STYLE}
          extra={
            canManageSupplierOrders && (
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={openSupplierOrderLinkModal}
              >
                {t('orders:supplierOrders.linkSupplierOrder')}
              </Button>
            )
          }
        >
          <Table
            dataSource={linkedSupplierOrders}
            rowKey={(record) => record.linkId || record.id}
            size="middle"
            pagination={false}
            scroll={{ x: 1240 }}
            locale={{ emptyText: t('orders:orderDetail.noLinkedSupplierOrders') }}
            columns={[
              {
                title: t('orders:supplierOrders.table.supplierOrderNumber'),
                key: 'orderNumber',
                render: (_value, record: SupplierOrderDto) => (
                  <Link to={`/app/supplier-orders/${record.id}`}>
                    {getSupplierOrderDisplayNumber(record)}
                  </Link>
                ),
              },
              {
                title: t('orders:supplierOrders.table.supplier'),
                key: 'supplier',
                render: (_value, record: SupplierOrderDto) => (
                  record.supplier?.name
                    ? <Link to={buildSupplierSearchPath(record.supplier.name)}>{record.supplier.name}</Link>
                    : '-'
                ),
              },
              {
                title: t('orders:supplierOrders.table.factory'),
                key: 'factory',
                render: (_value, record: SupplierOrderDto) => record.factory?.name || '-',
              },
              {
                title: t('orders:supplierOrders.form.supplierOrderItem'),
                key: 'supplierOrderItem',
                render: (_value, record: SupplierOrderDto) => {
                  if (!record.supplierOrderItemName) return '-';
                  if (!record.supplierOrderItemProductId) return record.supplierOrderItemName;

                  return (
                    <Link to={`/app/products/${record.supplierOrderItemProductId}`}>
                      {record.supplierOrderItemName}
                    </Link>
                  );
                },
              },
              {
                title: t('orders:supplierOrders.form.allocatedQuantity'),
                key: 'allocatedQuantity',
                align: 'right' as const,
                render: (_value, record: SupplierOrderDto) => formatSupplierAllocationQuantity(record),
              },
              ...(canViewSupplierOrderFinance ? [{
                title: t('orders:orderDetail.purchaseCost'),
                dataIndex: 'effectiveAllocatedPurchaseCostPLN',
                key: 'effectiveAllocatedPurchaseCostPLN',
                align: 'right' as const,
                render: (value: number | undefined, record: SupplierOrderDto) => (
                  isManualSupplierAllocation(record) ? t('orders:supplierOrders.detail.manualAllocation') : formatCurrency(value || 0, 'PLN')
                ),
              }, {
                title: t('orders:orderDetail.landedCost'),
                dataIndex: 'effectiveAllocatedLandedCostPLN',
                key: 'effectiveAllocatedLandedCostPLN',
                align: 'right' as const,
                render: (value: number | undefined, record: SupplierOrderDto) => (
                  isManualSupplierAllocation(record) ? '-' : formatCurrency(value || 0, 'PLN')
                ),
              }] : []),
            ]}
            summary={(data) => canViewSupplierOrderFinance ? (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5}>
                  <strong>{t('orders:orderDetail.total')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <strong>{formatCurrency(data.reduce((sum: number, item: SupplierOrderDto) => sum + Number(item.effectiveAllocatedPurchaseCostPLN || 0), 0), 'PLN')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">
                  <strong>{formatCurrency(data.reduce((sum: number, item: SupplierOrderDto) => sum + Number(item.effectiveAllocatedLandedCostPLN || 0), 0), 'PLN')}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            ) : null}
          />
          {canViewSupplierOrderFinance && linkedSupplierOrders.length > 0 && (
            <div className="order-supplier-links-total">
              <div>
                <div className="order-supplier-links-total__label">{t('orders:orderDetail.total')}</div>
                <div className="order-supplier-links-total__hint">
                  {t('orders:orderDetail.purchaseAndLandedCost')}
                </div>
              </div>
              <strong className="order-supplier-links-total__value">
                {formatCurrency(
                  linkedSupplierOrders.reduce(
                    (sum, supplierOrder) => sum + getSupplierAllocationTotalCost(supplierOrder),
                    0,
                  ),
                  'PLN',
                )}
              </strong>
            </div>
          )}
        </Card>
    </Tabs.TabPane>
    )}

    {(canViewCustomerOrderCostSection || canViewSalesDocuments || canViewCustomerSalePrices || canViewPurchaseDocuments || canViewMargin) && (
    <Tabs.TabPane
      tab={
        <span>
          <DollarOutlined />
          {t('orders:orderDetail.finances')}
          {(operationalCosts.length + purchaseInvoiceCosts.length + invoices.length) > 0 && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {operationalCosts.length + purchaseInvoiceCosts.length + invoices.length}
            </Tag>
          )}
        </span>
      }
      key="4"
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {canViewMargin && (
        <Card
          title={renderSectionTitle(t('orders:orderDetail.financialSummary'))}
          style={SECTION_CARD_STYLE}
          headStyle={SECTION_CARD_HEAD_STYLE}
          bodyStyle={{ ...SECTION_CARD_BODY_STYLE, paddingTop: 16 }}
        >
          <Row gutter={[16, 16]} className="order-financial-summary">
            <Col xs={24} md={8}>
              <div className={`order-financial-summary__margin order-financial-summary__margin--${marginTone}`}>
                <div className="order-financial-summary__eyebrow">
                  {t('orders:orderDetail.margin')}
                </div>
                <div className="order-financial-summary__margin-value">
                  {formatCurrency(marginDisplayAmount, 'PLN')}
                </div>
                <div className="order-financial-summary__margin-meta">
                  <span className="order-financial-summary__margin-rate">{marginPercentLabel}%</span>
                  <span className="order-financial-summary__status">
                    {t(`orders:orderDetail.${marginStatusKey}`)}
                  </span>
                </div>
              </div>
            </Col>
            <Col xs={24} md={16}>
              <div className="order-financial-summary__figures">
                <div className="order-financial-summary__figure">
                  <span className="order-financial-summary__figure-label">{t('orders:orderDetail.revenue')}</span>
                  <div className="order-financial-summary__figure-value">
                    <CurrencyAmount
                      amount={order.exchangeRate ? revenueDisplayAmount / order.exchangeRate : revenueDisplayAmount}
                      currency={order.currency}
                      plnAmount={revenueDisplayAmount}
                      rateDate={order.exchangeRateEffectiveDate || order.orderDate}
                      rateSource={order.exchangeRateSource}
                      rateTableNumber={order.exchangeRateTableNumber}
                      strong
                    />
                  </div>
                </div>
                <div className="order-financial-summary__figure">
                  <span className="order-financial-summary__figure-label">{t('orders:orderDetail.totalCost')}</span>
                  <strong className="order-financial-summary__figure-value">{formatCurrency(totalCostDisplayAmount, 'PLN')}</strong>
                </div>
                <div className="order-financial-summary__details">
                  {cogsDisplayAmount > 0 && (
                    <div className="order-financial-summary__detail-row">
                      <span>{t('orders:orderDetail.warehouseCostSummary')}</span>
                      <span>{formatCurrency(cogsDisplayAmount, 'PLN')}</span>
                    </div>
                  )}
                  {order.plannedMarginPercent != null && (
                    <>
                      <Divider style={{ margin: '4px 0' }} />
                      <div className="order-financial-summary__detail-row">
                        <span>{t('orders:orderDetail.plannedMargin')}</span>
                        <strong style={{ color: '#0f172a' }}>
                          {formatCurrency(plannedMarginDisplayAmount, 'PLN')} ({formatLocalizedPercent(order.plannedMarginPercent, i18n.language)})
                        </strong>
                      </div>
                      <div className="order-financial-summary__detail-row">
                        <span>{t('orders:orderDetail.marginDeviation')}</span>
                        <strong style={{ color: (marginDeviation || 0) >= 0 ? '#047857' : '#dc2626' }}>
                          {formatLocalizedNumber(marginDeviation || 0, 1, i18n.language)} pp
                        </strong>
                      </div>
                    </>
                  )}
                  {order.status === 'DELIVERED' && !order.isSettled && canSettleDeliveredOrder && (
                    <Button type="primary" style={{ justifySelf: 'start', borderRadius: 999, fontWeight: 600 }} onClick={handleSettleOrder}>
                      {t('orders:orderDetail.settleOrder')}
                    </Button>
                  )}
                  {order.isSettled && (
                    <>
                      <Divider style={{ margin: '4px 0' }} />
                      <div className="order-financial-summary__detail-row">
                        <span>{t('orders:orderDetail.settledAt')}</span>
                        <strong style={{ color: '#0f172a' }}>{formatDate(order.settledAt)}</strong>
                      </div>
                      <div className="order-financial-summary__detail-row">
                        <span>{t('orders:orderDetail.settledMargin')}</span>
                        <strong style={{ color: '#0f172a' }}>
                          {formatCurrency(settledMarginDisplayAmount, 'PLN')} ({formatLocalizedPercent(order.settledMarginPercent, i18n.language)})
                        </strong>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      )}
      {canViewMargin && (
        <OrderMarginExplanationSection
          marginExplanation={marginExplanation}
          loading={marginExplanationLoading}
        />
      )}
      {canViewCustomerOrderCostSection && (
      <Card
        title={renderSectionTitle(t('orders:orderDetail.orderCosts'))}
        style={SECTION_CARD_STYLE}
        headStyle={SECTION_CARD_HEAD_STYLE}
        bodyStyle={SECTION_CARD_BODY_STYLE}
        extra={
          <Space>
            {canEditCustomerOrderFallbackCosts && (
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAddCostModal}>
                {t('orders:orderDetail.addCost')}
              </Button>
            )}
            {canManageOrderOperations && canViewCustomerOrderFinancialCosts && (
              <Button
                size="small"
                icon={<FileTextOutlined />}
                onClick={async () => {
                  if (!id) return;

                  try {
                    const response: any = await generateWzFromOrder(id);
                    message.success(t('orders:orderDetail.messages.wzGeneratedSuccess'));
                    navigate(`/app/wz/${response.id}?mode=edit`);
                  } catch (error: any) {
                    message.error(error.response?.data?.error || t('orders:orderDetail.messages.wzGenerateFailed'));
                  }
                }}
              >
                {t('orders:orderDetail.generateWZ')}
              </Button>
            )}
          </Space>
        }
      >
        <Table
          dataSource={customerOrderCostRows}
          columns={operationalCostColumns}
          rowKey="id"
          size="middle"
          pagination={false}
          locale={{ emptyText: t('orders:orderDetail.noOrderCosts') }}
          summary={(data) => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}><strong>{t('orders:orderDetail.total')}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <strong>{formatCurrency(data.reduce((sum: number, cost: any) => sum + getCostAmountInPln(cost), 0), 'PLN')}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} colSpan={Math.max(operationalCostColumns.length - 2, 1)} />
            </Table.Summary.Row>
          )}
        />
      </Card>
      )}
      {canViewPurchaseDocuments && purchaseInvoiceCosts.length > 0 && (
      <Card
        title={renderSectionTitle(t('orders:orderDetail.purchaseInvoices'))}
        style={SECTION_CARD_STYLE}
        headStyle={SECTION_CARD_HEAD_STYLE}
        bodyStyle={SECTION_CARD_BODY_STYLE}
      >
        <Table
          dataSource={purchaseInvoiceCosts}
          columns={purchaseInvoiceCostColumns}
          rowKey="id"
          size="middle"
          pagination={false}
          locale={{ emptyText: t('orders:orderDetail.noPurchaseInvoices') }}
        />
      </Card>
      )}
      {canViewMargin && marginHistory.length > 0 && (
        <Card
          title={renderSectionTitle(t('orders:orderDetail.marginHistory'))}
          style={SECTION_CARD_STYLE}
          headStyle={SECTION_CARD_HEAD_STYLE}
          bodyStyle={{ ...SECTION_CARD_BODY_STYLE, paddingTop: 16 }}
          size="small"
        >
          <Timeline>
            {marginHistory.map((h) => {
              const historyMarginPercent = Number.isFinite(h.marginPercent) ? h.marginPercent : 0;

              return (
                <Timeline.Item key={h.id} color={historyMarginPercent < 15 ? 'red' : 'green'}>
                  <div>
                    <strong>{formatLocalizedPercent(historyMarginPercent, i18n.language)}</strong> ({formatCurrency(h.margin, 'PLN')})
                    <div style={{ fontSize: 12, color: '#999' }}>{h.reason} - {formatDateTime(h.changedAt)}</div>
                    {h.changedBy && <div style={{ fontSize: 12 }}>{h.changedBy.name}</div>}
                  </div>
                </Timeline.Item>
              );
            })}
          </Timeline>
        </Card>
      )}
      {canViewSalesDocuments && (
      <Card
        title={t('orders:orderDetail.salesInvoices')}
        extra={
          canEditSalesDocuments && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                invoiceForm.setFieldsValue({
                  type: 'PROFORMA',
                  totalAmount: undefined
                });
                setInvoiceModalVisible(true);
              }}
            >
              {t('orders:orderDetail.generateInvoice')}
            </Button>
          )
        }
      >
        {invoices.length > 0 ? (
          <Table
            dataSource={invoices}
            columns={invoiceColumns}
            rowKey="id"
            pagination={false}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <FileTextOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
            <p style={{ marginBottom: 20 }}>{t('orders:orderDetail.noInvoices')}</p>
            {canEditSalesDocuments && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  invoiceForm.setFieldsValue({
                    type: 'PROFORMA',
                    totalAmount: undefined
                  });
                  setInvoiceModalVisible(true);
                }}
              >
                {t('orders:orderDetail.generateFirstInvoice')}
              </Button>
            )}
          </div>
        )}
      </Card>
      )}
      </Space>
    </Tabs.TabPane>
    )}

    <Tabs.TabPane
      tab={
        <span>
          <CheckSquareOutlined />
          {t('tasks:orderDetailTab')}
          {orderTasks.length > 0 && <Tag color="blue" style={{ marginLeft: 8 }}>{orderTasks.length}</Tag>}
        </span>
      }
      key="5"
    >
      <Card
        title={t('tasks:orderDetailTab')}
        extra={
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => navigate(`/app/tasks`)}
          >
            {t('tasks:newTask')}
          </Button>
        }
      >
        {orderTasksLoading ? (
          <Spin />
        ) : orderTasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            {t('tasks:noTasks')}
          </div>
        ) : (
          <Table
            dataSource={orderTasks}
            rowKey="id"
            size="small"
            pagination={false}
            onRow={(record) => ({
              style: { cursor: 'pointer' },
              onClick: () => navigate('/app/tasks')
            })}
            columns={[
              {
                title: t('tasks:taskTitle'),
                dataIndex: 'title',
                key: 'title',
                render: (text: string) => <strong>{text}</strong>
              },
              {
                title: t('tasks:status'),
                dataIndex: 'status',
                key: 'status',
                width: 120,
                render: (status: string) => {
                  const colors: Record<string, string> = { NEW: 'default', IN_PROGRESS: 'processing', REVIEW: 'warning', DONE: 'success', CANCELLED: 'error' };
                  return <Tag color={colors[status]}>{t(`tasks:statuses.${status}`)}</Tag>;
                }
              },
              {
                title: t('tasks:priority'),
                dataIndex: 'priority',
                key: 'priority',
                width: 100,
                render: (priority: string) => {
                  const colors: Record<string, string> = { LOW: 'default', MEDIUM: 'blue', HIGH: 'orange', URGENT: 'red' };
                  return <Tag color={colors[priority]}>{t(`tasks:priorities.${priority}`)}</Tag>;
                }
              },
              {
                title: t('tasks:assignee'),
                dataIndex: 'assigneeName',
                key: 'assigneeName',
                width: 150,
                render: (name: string) => name ? <><UserOutlined /> {name}</> : <span style={{ color: '#999', fontStyle: 'italic' }}>{t('tasks:unassigned')}</span>
              },
              {
                title: t('tasks:dueDate'),
                dataIndex: 'dueDate',
                key: 'dueDate',
                width: 120,
                render: (date: string, record: InternalTask) => {
                  if (!date) return '-';
                  const [y, m, d] = date.split('-').map(Number);
                  const dueLocal = new Date(y, m - 1, d);
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  const isOverdue = dueLocal < today && record.status !== 'DONE' && record.status !== 'CANCELLED';
                  return <Tag color={isOverdue ? 'red' : undefined}><CalendarOutlined /> {dueLocal.toLocaleDateString()}</Tag>;
                }
              }
            ]}
          />
        )}
      </Card>
    </Tabs.TabPane>
  </Tabs>

      <Modal
        title={t('orders:supplierOrders.linkSupplierOrder')}
        open={supplierOrderLinkModalVisible}
        onCancel={closeSupplierOrderLinkModal}
        maskClosable={false}
        footer={null}
      >
        <Form
          form={supplierOrderLinkForm}
          layout="vertical"
          onFinish={handleLinkSupplierOrder}
          onFinishFailed={() => message.warning(t('orders:messages.fillRequired'))}
          scrollToFirstError
        >
          <Form.Item
            name="supplierOrderId"
            label={t('orders:supplierOrders.form.supplierOrder')}
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              loading={supplierOrderOptionsLoading}
              optionFilterProp="label"
              notFoundContent={
                supplierOrderOptionsLoading
                  ? <Spin size="small" />
                  : t('orders:supplierOrders.noAvailableSupplierOrders')
              }
              options={availableSupplierOrders.map((supplierOrder) => ({
                value: supplierOrder.id,
                label: [
                  getSupplierOrderDisplayNumber(supplierOrder),
                  supplierOrder.supplier?.name,
                  canViewSupplierOrderFinance ? formatCurrency(supplierOrder.totalAmountPLN || 0, 'PLN') : null,
                ].filter(Boolean).join(' - '),
              }))}
            />
          </Form.Item>
          <SupplierOrderLinkAllocationFields
            form={supplierOrderLinkForm}
            canEditSupplierOrderFinance={canEditSupplierOrderFinance}
            supplierOrderItemOptions={selectedSupplierOrderItemOptions}
          />
          <Form.Item name="notes" label={t('orders:notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={supplierOrderLinkSubmitting}>
                {t('common:save')}
              </Button>
              <Button onClick={closeSupplierOrderLinkModal} disabled={supplierOrderLinkSubmitting}>
                {t('common:cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('orders:orderDetail.generateInvoice')}
        open={invoiceModalVisible}
        onCancel={() => setInvoiceModalVisible(false)}
        maskClosable={false}
        footer={null}
      >
        <Form form={invoiceForm} layout="vertical" onFinish={handleGenerateInvoice} onFinishFailed={() => message.warning(t('orders:messages.fillRequired'))} scrollToFirstError>
          <Form.Item
            name="type"
            label={t('orders:orderDetail.invoiceType')}
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="PROFORMA">{t('orders:orderDetail.invoiceTypes.PROFORMA')}</Select.Option>
              <Select.Option value="ADVANCE">{t('orders:orderDetail.invoiceTypes.ADVANCE')}</Select.Option>
              <Select.Option value="FINAL">{t('orders:orderDetail.invoiceTypes.FINAL')}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="totalAmount"
            label={t('orders:orderDetail.totalAmount')}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              addonAfter={order?.currency || 'PLN'}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={invoiceSubmitting}>
                {t('orders:orderDetail.generate')}
              </Button>
              <Button onClick={() => setInvoiceModalVisible(false)}>
                {t('common:cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingCost ? t('orders:orderDetail.editCost') : t('orders:orderDetail.addCost')}
        open={costModalVisible}
        onCancel={closeCostModal}
        maskClosable={false}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleAddCost}>
          <Form.Item
            name="currency"
            label={t('orders:currency')}
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="USD">{t('orders:currencies.USD')}</Select.Option>
              <Select.Option value="EUR">{t('orders:currencies.EUR')}</Select.Option>
              <Select.Option value="PLN">{t('orders:currencies.PLN')}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="amount"
            label={t('orders:orderDetail.amount')}
            rules={[{ required: true }, { type: 'number', max: MAX_COST_AMOUNT }]}
          >
            <InputNumber min={0} max={MAX_COST_AMOUNT} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="description"
            label={t('orders:orderDetail.costDescription')}
            rules={[{ required: true }]}
          >
            <Input.TextArea />
          </Form.Item>
          <Form.Item
            name="document"
            label={t('orders:orderDetail.document')}
            valuePropName="fileList"
            getValueFromEvent={normalizeUploadFileList}
          >
            <Upload beforeUpload={() => false} maxCount={1}>
              <Button icon={<UploadOutlined />}>{t('orders:orderDetail.uploadDocument')}</Button>
            </Upload>
          </Form.Item>
          {editingCost?.documentUrl && (
            <div style={{ marginBottom: 16 }}>
              {t('orders:orderDetail.document')}: <a href={getAuthFileUrl(editingCost.documentUrl)} target="_blank" rel="noreferrer">{editingCost.documentName || t('orders:orderDetail.document')}</a>
            </div>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submittingCost}>
                {editingCost ? t('common:save') : t('orders:orderDetail.addCost')}
              </Button>
              <Button onClick={closeCostModal} disabled={submittingCost}>
                {t('common:cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('orders:orderDetail.updateStatus')}
        open={statusModalVisible}
        onCancel={() => {
          setStatusModalVisible(false);
          cancelConfirmation();
        }}
        maskClosable={false}
        footer={null}
      >
        <Form form={statusForm} layout="vertical" onFinish={handleUpdateStatus} onFinishFailed={() => message.warning(t('orders:messages.fillRequired'))} scrollToFirstError>
          <Form.Item
            name="status"
            label={t('orders:orderDetail.newStatus')}
            rules={[{ required: true }]}
          >
            <OrderStatusTransitionSelect
              statuses={CUSTOMER_ORDER_ACTIVE_STATUS_VALUES}
              currentStatus={order.status}
              allowedTransitions={getOrderAllowedTransitions(order)}
              getStatusLabel={(status) => t(`orders:statusOptions.${status}`)}
              currentLabel={t('orders:statusSelection.current')}
              unavailableLabel={t('orders:statusSelection.unavailable')}
            />
          </Form.Item>
          <Form.Item name="notes" label={t('orders:orderDetail.notes')}>
            <Input.TextArea />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={statusSubmitting}>{t('orders:orderDetail.updateStatus')}</Button>
          </Form.Item>
        </Form>
      </Modal>

      <CustomerEmailEditorModal
        title={t('orders:orderDetail.sendCustomerConfirmation')}
        open={customerEmailModalVisible}
        recipientName={order?.customer?.name}
        recipientEmail={order?.customer?.email}
        subject={customerEmailSubject}
        message={customerEmailMessage}
        previewHtml={customerEmailPreview?.html || ''}
        editing={customerEmailEditing}
        previewAvailable={Boolean(customerEmailPreview)}
        previewLoading={customerEmailPreviewLoading}
        sending={customerEmailSending}
        okText={t('orders:orderDetail.sendCustomerConfirmationOk')}
        previewErrorText={t('orders:orderDetail.customerConfirmationPreviewError')}
        onCancel={() => {
          setCustomerEmailModalVisible(false);
          setCustomerEmailEditing(false);
        }}
        onSubmit={handleSendCustomerConfirmation}
        onEdit={() => setCustomerEmailEditing(true)}
        onDiscardEdit={() => {
          setCustomerEmailEditing(false);
          setCustomerEmailPreview(customerEmailDefaultPreview);
          setCustomerEmailSubject(customerEmailDefaultPreview?.subject || '');
          setCustomerEmailMessage(customerEmailDefaultPreview?.message || '');
        }}
        onSubjectChange={setCustomerEmailSubject}
        onMessageChange={setCustomerEmailMessage}
      />

      <CustomerEmailEditorModal
        title={t('orders:orderDetail.sendCustomerStatusEmail')}
        open={statusEmailModalVisible}
        recipientName={order?.customer?.name}
        recipientEmail={order?.customer?.email}
        subject={statusEmailSubject}
        message={statusEmailMessage}
        previewHtml={statusEmailPreview?.html || ''}
        editing={statusEmailEditing}
        previewAvailable={Boolean(statusEmailPreview)}
        previewLoading={statusEmailPreviewLoading}
        sending={statusEmailSending}
        okText={t('orders:orderDetail.sendCustomerStatusEmailOk')}
        previewErrorText={t('orders:orderDetail.customerStatusEmailPreviewError')}
        onCancel={() => {
          setStatusEmailModalVisible(false);
          setStatusEmailEditing(false);
        }}
        onSubmit={handleSendCustomerStatusEmail}
        onEdit={() => setStatusEmailEditing(true)}
        onDiscardEdit={() => {
          setStatusEmailEditing(false);
          setStatusEmailPreview(statusEmailDefaultPreview);
          setStatusEmailSubject(statusEmailDefaultPreview?.subject || '');
          setStatusEmailMessage(statusEmailDefaultPreview?.message || '');
        }}
        onSubjectChange={setStatusEmailSubject}
        onMessageChange={setStatusEmailMessage}
      />

      <Modal
        title={t('orders:orderDetail.wzWarningTitle')}
        open={statusConfirmation.visible}
        onOk={confirmStatusChange}
        onCancel={cancelConfirmation}
        okText={t('orders:orderDetail.wzWarningOk')}
        cancelText={t('common:cancel')}
        confirmLoading={statusSubmitting}
      >
        <p>{statusConfirmation.message}</p>
        <p style={{ marginTop: 12, fontWeight: 'bold' }}>
          {t('orders:orderDetail.wzWarningConfirm')}
        </p>
      </Modal>
      </div>
    </div>
  );
};

export default OrderDetail;
